import React, { useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import swal from "sweetalert2";
import { traducirMessage } from "./blocks/traductor";
import useCommonApi from "./api/useCommonApi";

interface AutoLogoutProps {
    inactivityLimitInMinutes: number;
}

const AutoLogout: React.FC<AutoLogoutProps> = ({ inactivityLimitInMinutes }) => {
    const inactivityTimeout = useRef<number | null>(null);
    const dayChangeTimeout = useRef<number | null>(null);
    const dayCheckInitialized = useRef(false);
    const history = useHistory();
    const apiSource = useCommonApi();
    const currentToken = useRef<string | null>(sessionStorage.getItem("token") || null);

    const zIndex = 9999;
    const style = `
      .swal-container {
        z-index: ${zIndex} !important;
    }
    `;
    const styleElement = document.createElement("style");
    styleElement.textContent = style;
    document.head.appendChild(styleElement);

    const handleLogout = useCallback(async (reason: string) => {
        if (sessionStorage.getItem("logoutProcessed") === "true") {
            return;
        }
        sessionStorage.setItem("logoutProcessed", "true");

        if (reason === "inactivity") {
            localStorage.setItem(
                "logoutEvent",
                JSON.stringify({ token: currentToken.current, reason, timestamp: Date.now() })
            );
        }

        swal.fire({
            titleText: traducirMessage(11),
            text: traducirMessage(317),
            type: 'warning',
            showConfirmButton: true,
            customClass: {
                container: "swal-container",
            },
            confirmButtonAriaLabel: "SI",
        }).then(async () => {
            sessionStorage.clear();
            const parametros = await apiSource.obtenerAsync<any>("parametro/active");
            const loginUrl = parametros.find((a: any) => a.nombre === "URL Login Portal Nuevo");
            loginUrl ? window.location.replace(loginUrl.valor) : history.push("/login");
        });
    }, [apiSource, history, currentToken]);

    const resetInactivityTimeout = useCallback(() => {
        if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
        inactivityTimeout.current = window.setTimeout(() => {
            handleLogout("inactivity");
        }, inactivityLimitInMinutes * 60 * 1000);
    }, [inactivityLimitInMinutes, handleLogout]);

    const handleUserActivity = useCallback(() => resetInactivityTimeout(), [resetInactivityTimeout]);

    const parseServerDate = (dateStr: string): Date => {
        const [datePart, timePart] = dateStr.split(" ");
        const [day, month, year] = datePart.split("/").map(Number);
        const [hour, minute, second] = timePart.split(":").map(Number);
      
        return new Date(year, month - 1, day, hour, minute, second);
      };

      const scheduleDayChangeCheck = useCallback(async () => {
        if (dayCheckInitialized.current) return;
        dayCheckInitialized.current = true;
      
        try {
          const serverDateStr = await apiSource.obtenerAsync<any>("hora-actual/actual-date");
          const currentDate = parseServerDate(serverDateStr);
          const nextMidnight = new Date(currentDate);
          nextMidnight.setHours(24, 0, 0, 0);
          const diffMs = nextMidnight.getTime() - currentDate.getTime();
          dayChangeTimeout.current = window.setTimeout(() => {
            handleLogout("dayChange");
          }, diffMs);
        } catch (error) {
          console.error("Error al obtener hora servidor:", error);
          dayChangeTimeout.current = window.setTimeout(scheduleDayChangeCheck, 60 * 1000);
        }
      }, [apiSource, handleLogout]);

    const syncLogoutAcrossTabs = useCallback((event: StorageEvent) => {
        if (event.key === "logoutEvent") {
            const parsedEvent = JSON.parse(event.newValue || "{}");
            const { reason, token } = parsedEvent;
            if (sessionStorage.getItem("logoutProcessed") === "true") return;
            if (reason === "inactivity" && token === currentToken.current) {
                handleLogout(reason);
            }
        }
    }, [handleLogout, currentToken]);

    useEffect(() => {
        sessionStorage.removeItem("logoutProcessed");

        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        events.forEach((event) => window.addEventListener(event, handleUserActivity));
        window.addEventListener("storage", syncLogoutAcrossTabs);

        resetInactivityTimeout();
        scheduleDayChangeCheck();

        return () => {
            events.forEach((event) => window.removeEventListener(event, handleUserActivity));
            window.removeEventListener("storage", syncLogoutAcrossTabs);
            if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
            if (dayChangeTimeout.current) clearTimeout(dayChangeTimeout.current);
        };
    }, [resetInactivityTimeout, handleUserActivity, syncLogoutAcrossTabs, scheduleDayChangeCheck]);

    return null;
};

export default AutoLogout;